import { useState } from 'react';
import { Alert, Anchor, Button, Code, Select, Stack, Table, Text, Title } from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  useSuiteDependencies,
  useSuiteUsage,
} from '@/hooks/api/automationSuites/useAutomationSuites.ts';
import {
  ExactVersion,
  SuiteError,
  SuitePagination,
} from '@/pages/AutomationSuites/SuiteShared.tsx';
import type { SuiteDetail } from '@/types/automationSuites.ts';

const findings: Record<string, string> = {
  missing_observed_head: 'Some members have no version at the selected head.',
  dependency_unavailable: 'A dependency is missing, inaccessible or incorrectly pinned.',
  incomplete_call_contract: 'A call is missing part of its interface contract.',
  invalid_execution_graph: 'An execution graph could not be inspected.',
  unavailable_member: 'A member or dependency is retired or deleted.',
  dependency_cycle: 'A dependency cycle was found.',
  root_budget_exceeded: 'The member discovery budget was reached.',
  node_budget_exceeded: 'The dependency discovery budget was reached.',
  edge_budget_exceeded: 'The call discovery budget was reached.',
  step_budget_exceeded: 'The step inspection budget was reached.',
};

export default function SuiteContext({
  suite,
  refreshSuite,
}: {
  suite: SuiteDetail;
  refreshSuite?: () => void;
}) {
  const [basis, setBasis] = useState<'latest' | 'published'>('latest');
  const [page, setPage] = useState(1);
  const dependencies = useSuiteDependencies(suite.id, basis);
  const usage = useSuiteUsage(suite.id, page);
  const data = dependencies.data;
  return (
    <Stack>
      <Title order={2}>Dependencies and usage</Title>
      <Text c="dimmed">
        Observed exact heads and pinned calls. This inventory does not establish runtime readiness
        or a frozen Release.
      </Text>
      <Button
        variant="default"
        disabled={dependencies.isFetching || usage.isFetching}
        onClick={() => {
          refreshSuite?.();
          void dependencies.refetch();
          void usage.refetch();
        }}
      >
        Refresh inventory and usage
      </Button>
      <Select
        label="Inventory basis"
        value={basis}
        allowDeselect={false}
        data={[
          { value: 'latest', label: 'Latest versions' },
          { value: 'published', label: 'Published versions' },
        ]}
        onChange={(value) => setBasis(value === 'published' ? 'published' : 'latest')}
      />
      {!dependencies.isError && !dependencies.isFetchedAfterMount && (
        <Text>Loading dependency inventory…</Text>
      )}
      {dependencies.isError && (
        <SuiteError error={dependencies.error} retry={() => void dependencies.refetch()} />
      )}
      {!dependencies.isError && dependencies.isFetchedAfterMount && data && (
        <>
          {data.head_revision !== suite.head_revision && (
            <Alert color="orange">
              Suite membership changed between these observations. Refresh before relying on this
              inventory.
            </Alert>
          )}
          <Alert
            color={data.discovery_status === 'complete_inventory' ? 'blue' : 'orange'}
            title={
              data.discovery_status === 'access_limited'
                ? 'Access-limited inventory'
                : data.discovery_status === 'incomplete'
                  ? 'Incomplete inventory'
                  : 'Observed inventory completed'
            }
          >
            {data.discovery_status === 'complete_inventory'
              ? 'Structural discovery completed within its budgets. This is not a validation or assurance result.'
              : 'Only available authorized observations are shown. Missing rows do not prove that no dependency exists.'}
          </Alert>
          {data.findings.map((finding) => (
            <Text size="sm" key={finding}>
              {findings[finding] ?? `Inventory diagnostic: ${finding.replaceAll('_', ' ')}`}
            </Text>
          ))}
          {data.observed_heads
            .filter((head) => !head.automation_version_id)
            .map((head) => (
              <Text key={head.automation_id}>
                No {basis} head: <Code>{head.automation_id}</Code>
              </Text>
            ))}
          <Table.ScrollContainer minWidth={520}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Automation</Table.Th>
                  <Table.Th>Exact version</Table.Th>
                  <Table.Th>Relationship</Table.Th>
                  <Table.Th>Availability</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.nodes.map((node) => (
                  <Table.Tr key={node.automation_version_id}>
                    <Table.Td>
                      {node.availability === 'deleted' ? (
                        <Code>{node.automation_id}</Code>
                      ) : (
                        <Anchor component={Link} to={`/automations/${node.automation_id}`}>
                          {suite.members.results.find(
                            (member) => member.automation_id === node.automation_id,
                          )?.name ?? node.automation_id}
                        </Anchor>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <ExactVersion id={node.automation_version_id} number={node.version_number} />
                    </Table.Td>
                    <Table.Td>
                      {node.explicit_member ? 'Explicit member' : 'Derived dependency'}
                    </Table.Td>
                    <Table.Td>{node.availability}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {!data.nodes.length && <Text>No dependency nodes in this observation.</Text>}
          <Title order={3}>Pinned call sites</Title>
          {data.edges.map((edge, index) => (
            <Text
              size="sm"
              key={`${edge.source_automation_version_id}:${edge.source_step_id}:${index}`}
            >
              <Code>{edge.source_automation_version_id}</Code> · {edge.source_step_id} →{' '}
              <Code>{edge.target_automation_version_id}</Code> · {edge.execution_mode}
            </Text>
          ))}
          {!data.edges.length && <Text>No call edges in this observation.</Text>}
        </>
      )}
      <Title order={3}>Shared membership and callers</Title>
      {!usage.isError && !usage.isFetchedAfterMount && <Text>Loading usage…</Text>}
      {usage.isError && <SuiteError error={usage.error} retry={() => void usage.refetch()} />}
      {!usage.isError && usage.isFetchedAfterMount && usage.data && (
        <>
          {usage.data.access_scope === 'granted_automations' && (
            <Text c="orange">Usage is limited to authorized automations.</Text>
          )}
          {usage.data.results.map((item, index) => (
            <Text
              size="sm"
              key={`${item.kind}:${item.suite_id}:${item.automation_id}:${item.automation_version_id}:${index}`}
            >
              {item.kind === 'shared_membership' && item.suite_id ? (
                <>
                  Member <Code>{item.automation_id}</Code> also belongs to{' '}
                  <Anchor component={Link} to={`/automation-suites/${item.suite_id}`}>
                    another Suite
                  </Anchor>
                  .
                </>
              ) : (
                <>
                  <Anchor component={Link} to={`/automations/${item.automation_id}`}>
                    Caller automation
                  </Anchor>{' '}
                  · <ExactVersion id={item.automation_version_id} number={item.version_number} /> (
                  {item.is_latest_head ? 'latest' : 'historical'}
                  {item.is_published_head ? ', published' : ''})
                </>
              )}
            </Text>
          ))}
          {!usage.data.results.length && <Text>No usage rows in this authorized view.</Text>}
          <SuitePagination data={usage.data} onPage={setPage} />
        </>
      )}
    </Stack>
  );
}
